// DateInputField.tsx
import { useFormContext, useWatch } from "react-hook-form";
import { useEffect, useState, useRef } from "react";
import { type FormField } from "../formFieldType";
import MyBox from "../../MyBox";
import { Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";
import { format, parse, isValid, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, isBefore, startOfToday } from "date-fns";

interface DateInputFieldProps {
  field: FormField;
}

export function DateInputField({ field }: DateInputFieldProps) {
  const { register, setValue, control, formState } = useFormContext();
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const value = useWatch({
    control,
    name: field.name,
  });

  const error = formState.errors[field.name];
  const minDate = field.min ? new Date(field.min) : null;
  const maxDate = field.max ? new Date(field.max) : null;
  const placeholder = field.placeholder || "Select date";

  // Initialize selected date from form value
  useEffect(() => {
    if (value && typeof value === 'string') {
      const parsedDate = parse(value, 'yyyy-MM-dd', new Date());
      if (isValid(parsedDate)) {
        setSelectedDate(parsedDate);
        setCurrentMonth(parsedDate);
      }
    } else if (value instanceof Date && isValid(value)) {
      setSelectedDate(value);
      setCurrentMonth(value);
    }
  }, [value]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node) &&
          inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDateSelect = (date: Date) => {
    // Check min/max constraints
    if (minDate && isBefore(date, minDate)) return;
    if (maxDate && isBefore(maxDate, date)) return;
    
    setSelectedDate(date);
    const formattedDate = format(date, 'yyyy-MM-dd');
    setValue(field.name, formattedDate, { 
      shouldDirty: true, 
      shouldValidate: true 
    });
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const parsedDate = parse(inputValue, 'yyyy-MM-dd', new Date());
    
    if (isValid(parsedDate)) {
      // Check min/max constraints
      if (minDate && isBefore(parsedDate, minDate)) return;
      if (maxDate && isBefore(maxDate, parsedDate)) return;
      
      setSelectedDate(parsedDate);
      setValue(field.name, inputValue, { shouldDirty: true, shouldValidate: true });
    } else {
      setValue(field.name, inputValue, { shouldDirty: true, shouldValidate: true });
    }
  };

  const handleClear = () => {
    setSelectedDate(null);
    setValue(field.name, "", { shouldDirty: true, shouldValidate: true });
    setIsOpen(false);
  };

  const nextMonth = () => setCurrentMonth(prev => addMonths(prev, 1));
  const prevMonth = () => setCurrentMonth(prev => subMonths(prev, 1));

  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  };

  const isDateDisabled = (date: Date): boolean => {
    if (minDate && isBefore(date, minDate)) return true;
    if (maxDate && isBefore(maxDate, date)) return true;
    return false;
  };

  const formatDisplayDate = (): string => {
    if (!selectedDate) return '';
    return format(selectedDate, 'PPP'); // e.g., "Jan 1, 2024"
  };

  return (
    <MyBox topText={field.label} required={field.required}>
      <div className="space-y-2">
        {/* Date Input with Calendar Button */}
        <div className="relative">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                ref={inputRef}
                type="text"
                value={value || ''}
                onChange={handleInputChange}
                placeholder={placeholder}
                disabled={field.disabled}
                className={`
                  w-full px-3 py-2 pl-10 pr-8
                  border rounded-lg
                  bg-white dark:bg-gray-800
                  text-gray-900 dark:text-gray-100
                  placeholder-gray-400 dark:placeholder-gray-500
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                  transition-all duration-200
                  ${error 
                    ? "border-red-500 dark:border-red-400 focus:ring-red-500" 
                    : "border-gray-300 dark:border-gray-600 focus:border-blue-500"
                  }
                  ${field.disabled ? "opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-900" : ""}
                `}
              />
              <Calendar 
                className={`
                  absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4
                  ${error ? 'text-red-500' : 'text-gray-400 dark:text-gray-500'}
                  ${field.disabled ? 'opacity-50' : 'cursor-pointer'}
                `}
                onClick={() => !field.disabled && setIsOpen(!isOpen)}
              />
              {value && !field.disabled && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <X className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                </button>
              )}
            </div>
            
            {/* Display formatted date */}
            {selectedDate && (
              <div className="hidden sm:block text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {formatDisplayDate()}
              </div>
            )}
          </div>
        </div>

        {/* Calendar Dropdown */}
        {isOpen && !field.disabled && (
          <div 
            ref={calendarRef}
            className="absolute z-50 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 w-full max-w-sm"
            style={{ minWidth: '280px' }}
          >
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={prevMonth}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              
              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                {format(currentMonth, 'MMMM yyyy')}
              </div>
              
              <button
                type="button"
                onClick={nextMonth}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {getDaysInMonth().map((date, idx) => {
                const isSelected = selectedDate && isSameDay(date, selectedDate);
                const isCurrentMonth = isSameMonth(date, currentMonth);
                const isTodayDate = isToday(date);
                const disabled = isDateDisabled(date);
                
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => !disabled && handleDateSelect(date)}
                    disabled={disabled}
                    className={`
                      h-8 sm:h-10 text-sm rounded-lg
                      flex items-center justify-center
                      transition-all duration-200
                      ${!isCurrentMonth ? 'text-gray-400 dark:text-gray-600' : ''}
                      ${isSelected 
                        ? 'bg-blue-600 text-white hover:bg-blue-700' 
                        : isTodayDate && !isSelected
                        ? 'border-2 border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100'
                      }
                      ${disabled ? 'opacity-40 cursor-not-allowed line-through' : 'cursor-pointer'}
                    `}
                  >
                    {format(date, 'd')}
                  </button>
                );
              })}
            </div>

            {/* Quick Actions */}
            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const today = new Date();
                  if (!isDateDisabled(today)) {
                    handleDateSelect(today);
                  }
                }}
                className="flex-1 text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Today
              </button>
              <button
                type="button"
                onClick={handleClear}
                className="flex-1 text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Helper Text */}
        {field.helperText && !error && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {field.helperText}
          </p>
        )}

        {/* Error Message */}
        {error && (
          <p className="text-xs text-red-600 dark:text-red-400">
            {String(error.message)}
          </p>
        )}

        {/* Date Range Display */}
        {minDate && maxDate && (
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Select date between {format(minDate, 'MMM d, yyyy')} and {format(maxDate, 'MMM d, yyyy')}
          </p>
        )}
      </div>
    </MyBox>
  );
}